import React, { useRef, useState, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Form, Row, Col, Modal, Button } from 'react-bootstrap';
import sem_imagem from '../../assets/images/sem_imagem.png';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useProvider, isNative } from '../../context/MenuProvider';

const ImageCropper = ({ id, radius = 0, so_exibe = false, setFoto, foto, label, width = 385, height = 560, className, xs=5, md=4 }) => {
  const [image, setImage] = useState(null);
  const [tempImage, setTempImage] = useState(null); 
  const [showModal, setShowModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const {
    site
  } = useProvider();
  // Carrega a imagem inicial vinda das props
  useEffect(() => {
    if (foto) {
      if (typeof foto === 'string') {
        //const urlFoto = foto.startsWith('/') ? `https://${site?.domain}${foto}` : foto;
        setImage(foto);
      } else if (foto instanceof File || foto instanceof Blob) {
        setImage(URL.createObjectURL(foto));
      }
    }
  }, [foto]);

  // Função para reduzir o peso da imagem ANTES de abrir o editor (evita erro de memória)
  const compressImage = (url) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Limita a imagem para no máximo 1200px (suficiente para qualidade e leve para RAM)
        const MAX_WIDTH = 1200;
        const MAX_HEIGHT = 1200;
        let w = img.width;
        let h = img.height;

        if (w > h) {
          if (w > MAX_WIDTH) {
            h *= MAX_WIDTH / w;
            w = MAX_WIDTH;
          }
        } else {
          if (h > MAX_HEIGHT) {
            w *= MAX_HEIGHT / h;
            h = MAX_HEIGHT;
          }
        }

        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.7)); 
      };
    });
  };

  // Handler para cliques no Nativo (Capacitor)
  const handleNativeClick = async () => {
    try {
      const captured = await Camera.getPhoto({
        quality: 60,
        allowEditing: false, 
        resultType: CameraResultType.Uri,
        source: CameraSource.Prompt,
        promptLabelHeader: 'Selecionar Foto',
        promptLabelPhoto: 'Da Galeria',
        promptLabelPicture: 'Tirar Foto'
      });

      if (captured && captured.webPath) {
        const compressed = await compressImage(captured.webPath);
        setTempImage(compressed);
        setShowModal(true);
      }
    } catch (err) {
      console.warn("Câmera nativa cancelada");
    }
  };

  // Handler para cliques no PWA (Navegador)
  const handleWebClick = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const rawUrl = URL.createObjectURL(file);
    const compressed = await compressImage(rawUrl);
    setTempImage(compressed);
    setShowModal(true);
    e.target.value = ''; 
  };

  const onCropComplete = useCallback((_area, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  // Gera a imagem final recortada
  const handleSaveCrop = async () => {
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.src = tempImage;
    
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;

      // Desenha apenas a parte recortada
      ctx.drawImage(
        img,
        croppedAreaPixels.x, croppedAreaPixels.y,
        croppedAreaPixels.width, croppedAreaPixels.height,
        0, 0, width, height
      );

      canvas.toBlob((blob) => {
        const file = new File([blob], `crop-${id}-${Date.now()}.webp`, { type: 'image/webp' });
        setImage(URL.createObjectURL(blob));
        if (setFoto) setFoto(file);
        setShowModal(false);
      }, 'image/webp', 0.8);
    };
  };

  return (
    <Row>
      <Col xs={xs} md={md} className={className || "mx-auto"}>
        <div 
           className="canvas-container" 
           onClick={() => {
             if (so_exibe) return;
             if (isNative) {
               handleNativeClick();
             } else {
               document.getElementById(`file-${id}`).click();
             }
           }}
           style={{ 
             cursor: 'pointer', 
             borderRadius: `${radius}%`, 
             overflow: 'hidden', 
             border: '1px solid #ddd', 
             backgroundImage: `url(${image || sem_imagem})`, 
             backgroundSize: 'cover', 
             backgroundPosition: 'center',
             position: 'relative'
           }}
        >
          {/* Mantém a proporção do container */}
          <div style={{ paddingTop: `${(height / width) * 100}%` }} />
        </div>
        
        <input 
          type="file" 
          id={`file-${id}`} 
          hidden 
          onChange={handleWebClick} 
          accept="image/*" 
        />
      </Col>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Ajustar Imagem</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '400px', position: 'relative', background: '#333' }}>
          {tempImage && (
            <Cropper
              image={tempImage}
              crop={crop}
              zoom={zoom}
              aspect={width / height}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 px-3 mb-2">
            <label className="text-muted small">Zoom</label>
            <input 
              type="range" 
              className="form-range" 
              min={1} max={3} step={0.1} 
              value={zoom} 
              onChange={(e) => setZoom(parseFloat(e.target.value))} 
            />
          </div>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSaveCrop}>Confirmar Corte</Button>
        </Modal.Footer>
      </Modal>

      <style jsx>{`
        .canvas-container {
            transition: all 0.2s;
        }
        .canvas-container:hover {
          opacity: 0.9;
          border-color: #007bff !important;
          box-shadow: 0 0 8px rgba(0,123,255,0.2);
        }
      `}</style>
    </Row>
  );
};

export default ImageCropper;
